import { FormEvent, PropsWithChildren, useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'exercise-toolbox:prelaunch-access-v1'
const PASSWORD = (import.meta.env.VITE_PRELAUNCH_PASSWORD || '').trim()

type GateStatus = 'checking' | 'locked' | 'unlocked'

const toSha256 = async (value: string) => {
  if (!crypto?.subtle) throw new Error('Crypto API unavailable')
  const data = new TextEncoder().encode(value)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function PrelaunchGate({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<GateStatus>(PASSWORD ? 'checking' : 'unlocked')
  const [envHash, setEnvHash] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  const disabled = !PASSWORD

  useEffect(() => {
    if (disabled) {
      setStatus('unlocked')
      return
    }

    let cancelled = false

    const bootstrap = async () => {
      try {
        const hashed = await toSha256(PASSWORD)
        if (cancelled) return
        setEnvHash(hashed)
        const stored = localStorage.getItem(STORAGE_KEY)
        setStatus(stored === hashed ? 'unlocked' : 'locked')
      } catch (err) {
        console.warn('[PrelaunchGate] Failed to initialize gate', err)
        if (!cancelled) setStatus('locked')
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [disabled])

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setError(null)

      if (disabled) {
        setStatus('unlocked')
        return
      }

      if (!envHash) {
        setError('Unable to verify password right now. Please try again.')
        return
      }

      try {
        const providedHash = await toSha256(input.trim())
        if (providedHash === envHash) {
          localStorage.setItem(STORAGE_KEY, envHash)
          setStatus('unlocked')
        } else {
          setError('Incorrect password. Please try again.')
        }
      } catch (err) {
        console.warn('[PrelaunchGate] Password verification failed', err)
        setError('Something went wrong. Please retry.')
      }
    },
    [disabled, envHash, input]
  )

  const handleReset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setInput('')
    setError(null)
    if (!disabled) setStatus('locked')
  }, [disabled])

  if (status === 'unlocked') {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center font-bold">
            ET
          </div>
          <div>
            <p className="text-sm text-white/70 uppercase tracking-wider">Exercise Toolbox</p>
            <h1 className="text-xl font-semibold">Preview Access</h1>
          </div>
        </div>

        <p className="text-white/80 text-sm mb-6">
          Enter the preview password to continue. This gate is for prelaunch access only.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-sm text-white/70">Password</span>
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="mt-2 w-full rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="Enter password"
              required
              autoFocus
              autoComplete="off"
            />
          </label>

          {error && <p className="text-sm text-red-300">{error}</p>}

          <button
            type="submit"
            className="w-full py-3 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 font-semibold shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 transition"
            disabled={status === 'checking'}
          >
            {status === 'checking' ? 'Checking...' : 'Enter' }
          </button>
        </form>

        <button
          type="button"
          onClick={handleReset}
          className="mt-4 text-sm text-white/70 hover:text-white"
        >
          Not you? Reset access
        </button>
      </div>
    </div>
  )
}
